import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  EmptyStation,
  type FormActionData,
  InfoPanel,
  MessageBar,
  type Nullable,
  Paragraph,
  ReadOnlyField,
  Section,
  type StationMessage,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, useFormikContext } from 'formik';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  RmqConnectionDocument,
  type RmqConnectionPayload,
  type RmqConnectionQuery,
  type RmqConnectionQueryVariables,
} from '../../../../generated/hosting-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';

type FormData = Nullable<RmqConnectionPayload>;
type RmqConnectionData = RmqConnectionQuery['rmqConnection'];
const notGeneratedValue = 'Not Generated';

export const RMQConnectionDetail: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  // Query messaging setup
  const [message, setMessage] = useState<StationMessage>();
  const [error, setError] = useState<string>();
  const [rmqConnectionData, setRMQConnectionData] = useState<
    RmqConnectionData | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of hosting-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.HOSTING_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          const apolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
          );

          const result = await apolloClient.query<
            RmqConnectionQuery,
            RmqConnectionQueryVariables
          >({
            query: RmqConnectionDocument,
            fetchPolicy: 'network-only',
          });

          if (result.errors) {
            setError(result.errors[0].message);
          } else {
            if (result.data.rmqConnection) {
              if (
                result.data.rmqConnection.rmqVHost === null ||
                result.data.rmqConnection.rmqUser === null
              ) {
                setMessage({
                  title:
                    'A RabbitMQ user has not been generated for this environment. Please use the Generate User action to perform this setup. Once generated, the RabbitMQ user credentials will be available here.',
                  type: 'info',
                });
              }

              setRMQConnectionData({
                ...result.data.rmqConnection,
                rmqVHost:
                  result.data.rmqConnection.rmqVHost ?? notGeneratedValue,
                rmqUser: result.data.rmqConnection.rmqUser ?? notGeneratedValue,
                rmqPassword:
                  result.data.rmqConnection.rmqPassword ?? notGeneratedValue,
              });
            }
          }
        }
      } catch (_e) {
        setMessage({
          title: `An error occurred while trying to communicate with the RabbitMQ broker. Please try again.`,
          type: 'info',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const Form: React.FC = () => {
    return (
      <>
        {rmqConnectionData && (
          <>
            <Field name="rmqProtocol" label="Protocol" as={ReadOnlyField} />
            <Field name="rmqHost" label="Host" as={ReadOnlyField} />
            <Field name="rmqPort" label="Port" as={ReadOnlyField} />
            <Field
              name="rmqMgmtProtocol"
              label="Management Protocol"
              as={ReadOnlyField}
            />
            <Field
              name="rmqMgmtHost"
              label="Management Host"
              as={ReadOnlyField}
            />
            <Field
              name="rmqMgmtPort"
              label="Management Port"
              as={ReadOnlyField}
            />
            <Field name="rmqVHost" label="Vhost" as={ReadOnlyField} />

            <Field name="rmqUser" label="User Name" as={ReadOnlyField} />

            <Field name="rmqPassword" label="Password" as={ReadOnlyField} />
          </>
        )}
      </>
    );
  };

  const { actions } = useActions(
    rmqConnectionData?.rmqUser !== notGeneratedValue,
    envId,
  );

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="RabbitMQ Details">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<FormData>
          defaultTitle="RabbitMQ Details"
          actions={actions}
          initialData={{
            data: rmqConnectionData,
            loading,
            error,
          }}
          saveData={() => {
            // This station is read-only, and any mutation is done via actions
          }}
          infoPanel={rmqConnectionData ? <Panel /> : undefined}
          stationMessage={message}
        >
          <Form />
        </Details>
      )}
    </>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<FormData>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Tenant ID">{values?.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values?.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

function useActions(
  userExists: boolean,
  envId: string,
): {
  readonly actions: FormActionData<FormData>[];
} {
  const history = useHistory();

  const actions: FormActionData<FormData>[] = [];

  if (!userExists) {
    actions.push({
      label: 'Generate User',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'This will generate a new RabbitMQ user for the environment. Confirm to continue.',
      },
      onActionSelected: () => {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/rabbitmq/resetPassword`,
        );
      },
    });
  } else {
    actions.push({
      label: 'Password',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/rabbitmq/resetPassword`,
    });
  }

  return { actions } as const;
}

export const RMQConnectionDetailCrumb: BreadcrumbResolver = () => 'RabbitMQ';
